import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { Book } from "@phosphor-icons/react";
import { api } from "@alfred/convex/_generated/api";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Badge } from "~/components/ui/badge";
import { relativeTime } from "~/lib/utils";

export const Route = createFileRoute("/settings")({
  component: SettingsPage,
});

function SettingsPage() {
  const settings = useQuery(api.settings.all, {});
  const repos = useQuery(api.repos.list, {});
  const brief = useQuery(api.briefs.get, {});
  // The engine publishes the model specs it actually loaded when the
  // dispatcher boots. Read those instead of hardcoding the strings here —
  // the hardcoded copy drifted from the real env across several model bumps.
  const model = (key: string): string => {
    if (!settings) return "loading…";
    const row = settings.find((s) => s.key === key);
    return typeof row?.value === "string" ? row.value : "(not reported yet)";
  };
  return (
    <div className="mx-auto max-w-3xl space-y-4 px-4 py-6 lg:px-6">
      <Card>
        <CardHeader>
          <CardTitle>Tech Brief</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p className="text-muted-foreground">
            Authoritative Paul's Job context Alfred injects into every Cursor
            prompt. The brief also drives Alfred's repo allowlist — only
            repositories mentioned as <code className="rounded bg-muted px-1 py-0.5">hyrdrocks/&lt;name&gt;</code> in
            the brief are visible to the repo picker. Edit it live — changes
            apply on the next ticket and the next repo sync.
          </p>
          <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-card px-3 py-2">
            <span className="text-xs text-muted-foreground">Status:</span>
            <Badge variant={brief?.hasContent ? "success" : "warn"}>
              {brief?.hasContent ? "Loaded" : "Empty"}
            </Badge>
            {brief?.updatedAt ? (
              <span className="text-xs text-muted-foreground">
                Updated {relativeTime(brief.updatedAt)}
                {brief.updatedBy ? ` by ${brief.updatedBy}` : ""}
              </span>
            ) : null}
            <Link
              to="/brief"
              className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
            >
              <Book size={12} weight="bold" />
              Open editor
            </Link>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Configuration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <Row label="Coder model" value={model("model.coder")} />
          <Row label="Planner model" value={model("model.planner")} />
          <Row label="Repo-picker model" value={model("model.picker")} />
          <Row label="PR-reviewer model" value={model("model.reviewer")} />
          <Row
            label="Convex URL"
            value={import.meta.env.VITE_CONVEX_URL ?? "(not set)"}
            mono
          />
          <Row
            label="Repo catalogue size"
            value={`${repos?.length ?? "loading…"} (brief-driven allowlist)`}
          />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Runtime flags</CardTitle>
        </CardHeader>
        <CardContent>
          {(() => {
            // Hide settings rows that have their own dedicated UI (the tech
            // brief and model specs are rendered above; sync-request
            // timestamps are internal).
            const HIDDEN_PREFIXES = ["techBrief.", "model."];
            const HIDDEN_KEYS = new Set([
              "jiraSyncRequestedAt",
              "repoSyncRequestedAt",
            ]);
            const visible = (settings ?? []).filter(
              (s) =>
                !HIDDEN_KEYS.has(s.key) &&
                !HIDDEN_PREFIXES.some((p) => s.key.startsWith(p)),
            );
            if (!settings) {
              return (
                <p className="text-sm text-muted-foreground">Loading…</p>
              );
            }
            if (visible.length === 0) {
              return (
                <p className="text-sm text-muted-foreground">
                  No runtime flags yet. The engine writes these as it runs.
                </p>
              );
            }
            return (
              <ul className="space-y-2 text-sm">
                {visible.map((s) => {
                  const rendered =
                    typeof s.value === "object"
                      ? JSON.stringify(s.value)
                      : String(s.value);
                  const display =
                    rendered.length > 60
                      ? `${rendered.slice(0, 60)}…`
                      : rendered;
                  return (
                    <li
                      key={s.key}
                      className="flex items-center justify-between gap-2 rounded-xl border border-border bg-card px-3 py-2"
                    >
                      <span className="font-mono text-xs">{s.key}</span>
                      <Badge variant="muted" title={rendered}>
                        {display}
                      </Badge>
                    </li>
                  );
                })}
              </ul>
            );
          })()}
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>How Alfred works</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>
            <strong className="text-foreground">1. Watch.</strong> A worker
            polls Jira every minute for tickets in your project that carry the{" "}
            <code className="rounded bg-muted px-1 py-0.5">alfred</code> label.
          </p>
          <p>
            <strong className="text-foreground">2. Decide.</strong> When you (or
            the engine) trigger a run, a Cursor agent reads the ticket plus the
            catalogue of repos in the org and writes back a single best pick.
          </p>
          <p>
            <strong className="text-foreground">3. Build.</strong> Alfred clones
            that repo locally, creates a branch named{" "}
            <code className="rounded bg-muted px-1 py-0.5">
              alfred/&lt;jira-key&gt;-&lt;slug&gt;
            </code>
            , and runs a Cursor local agent against it.
          </p>
          <p>
            <strong className="text-foreground">4. Ship.</strong> Changes are
            committed, pushed, and a PR is opened. The Kanban card moves to{" "}
            <em>In Review</em>. When the PR is approved, it advances to{" "}
            <em>QA Review</em>; merged → <em>Done</em>; closed without merge →{" "}
            <em>QA Failed</em>.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function Row({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-2 rounded-xl border border-border bg-card px-3 py-2">
      <span className="text-muted-foreground">{label}</span>
      <span className={mono ? "font-mono text-xs" : "text-xs"}>{value}</span>
    </div>
  );
}
